import { useCallback } from "react";
import { createViewCompanySettings } from "@/modules/rev_eu/delivery/views/ViewCompanySettings";
import type {
    TCompanySftpSettings,
    TCompanyYardiMasterDataSettings,
    TFileProcessingThreshold,
    TSftpDeliveryInstance,
    TSftpEnvironmentDefaults,
    TSftpFileSubmission,
    TYardiMasterDataSyncRun,
} from "@/store";
import { computeIsViewingAsAdmin } from "@/store/auth/sliceAuth";
import type {
    TApiCompanySftpSettings,
    TApiCompanyYardiMasterDataSettings,
    TApiFileProcessingThreshold,
    TApiSftpEnvironmentDefaults,
    TApiSftpFileSubmission,
    TApiYardiMasterDataSyncRun,
} from "@/apis/proda_finance";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

const mapThreshold = (item: TApiFileProcessingThreshold): TFileProcessingThreshold => ({
    id: item.id,
    companyId: item.company_id,
    startTime: item.start_time,
    endTime: item.end_time,
    threshold: item.threshold,
    instance: item.instance,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
});

const mapSftpSettings = (item: TApiCompanySftpSettings): TCompanySftpSettings => ({
    id: item.id,
    companyId: item.company_id,
    enabled: item.enabled,
    remoteDirectory: item.remote_directory,
    fileNamePattern: item.file_name_pattern,
    notes: item.notes,
    host: item.host,
    port: item.port,
    username: item.username,
    knownHosts: item.known_hosts,
    hasPassword: item.has_password,
    hasPrivateKey: item.has_private_key,
    hasPrivateKeyPassphrase: item.has_private_key_passphrase,
    passwordSha256: item.password_sha256,
    privateKeySha256: item.private_key_sha256,
    privateKeyPassphraseSha256: item.private_key_passphrase_sha256,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
});

const mapSftpEnvDefaults = (item: TApiSftpEnvironmentDefaults): TSftpEnvironmentDefaults => ({
    host: item.host,
    port: item.port,
    username: item.username,
    hasPassword: item.has_password,
    hasPrivateKey: item.has_private_key,
    hasPrivateKeyPassphrase: item.has_private_key_passphrase,
    hasKnownHosts: item.has_known_hosts,
    passwordSha256: item.password_sha256,
    privateKeySha256: item.private_key_sha256,
    privateKeyPassphraseSha256: item.private_key_passphrase_sha256,
});

const mapSftpSubmission = (item: TApiSftpFileSubmission): TSftpFileSubmission => ({
    id: item.id,
    financialFileId: item.financial_file_id,
    fileName: item.file_name,
    fileType: item.file_type,
    periodLabel: item.period_label,
    totalLineItems: item.total_line_items,
    uniqueKey: item.unique_key,
    instance: item.instance,
    status: item.status,
    errorMessage: item.error_message,
    triggeredBy: item.triggered_by,
    triggeredAt: item.triggered_at,
    updatedAt: item.updated_at,
    events: item.events.map((event) => ({
        status: event.status,
        detail: event.detail,
        occurredAt: event.occurred_at,
    })),
    transfers: item.transfers.map((transfer) => ({
        id: transfer.id,
        status: transfer.status,
        remotePath: transfer.remote_path,
        sizeBytes: transfer.size_bytes,
        durationMs: transfer.duration_ms,
        errorMessage: transfer.error_message,
        startedAt: transfer.started_at,
        completedAt: transfer.completed_at,
        hasContent: transfer.has_content,
    })),
});

const mapYardiMasterDataSettings = (
    item: TApiCompanyYardiMasterDataSettings,
): TCompanyYardiMasterDataSettings => ({
    id: item.id,
    companyId: item.company_id,
    baseUrl: item.base_url,
    hasBearerToken: item.has_bearer_token,
    bearerTokenSha256: item.bearer_token_sha256,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
});

const mapYardiMasterDataSyncRun = (item: TApiYardiMasterDataSyncRun): TYardiMasterDataSyncRun => ({
    id: item.id,
    status: item.status as TYardiMasterDataSyncRun["status"],
    trigger: item.trigger as TYardiMasterDataSyncRun["trigger"],
    startedAt: item.started_at,
    finishedAt: item.finished_at,
    error: item.error,
    objectCounts: item.object_counts,
});

export const wireSettingsCompany = ({ store: _store, useAppStore, api }: TCompositionDeps) => {
    const ViewCompanySettings = createViewCompanySettings({
        useCases: () => {
            const thresholdsState = useAppStore((state) => state.fileProcessingThresholdsState);
            const sftpSettingsState = useAppStore((state) => state.companySftpSettingsState);
            const sftpSubmissionsState = useAppStore((state) => state.sftpFileSubmissionsState);
            const setThresholdsLoading = useAppStore(
                (state) => state.setFileProcessingThresholdsLoading,
            );
            const setThresholds = useAppStore((state) => state.setFileProcessingThresholds);
            const setThresholdsError = useAppStore(
                (state) => state.setFileProcessingThresholdsError,
            );
            const setSftpSettingsLoading = useAppStore(
                (state) => state.setCompanySftpSettingsLoading,
            );
            const setSftpSettings = useAppStore((state) => state.setCompanySftpSettings);
            const setSftpSettingsError = useAppStore((state) => state.setCompanySftpSettingsError);
            const sftpEnvDefaults = useAppStore((state) => state.sftpEnvironmentDefaults);
            const setSftpEnvDefaults = useAppStore((state) => state.setSftpEnvironmentDefaults);
            const setSftpSubmissionsLoading = useAppStore(
                (state) => state.setSftpFileSubmissionsLoading,
            );
            const setSftpSubmissions = useAppStore((state) => state.setSftpFileSubmissions);
            const setSftpSubmissionsError = useAppStore(
                (state) => state.setSftpFileSubmissionsError,
            );
            const masterDataSettingsState = useAppStore(
                (state) => state.companyYardiMasterDataSettingsState,
            );
            const setMasterDataSettingsLoading = useAppStore(
                (state) => state.setCompanyYardiMasterDataSettingsLoading,
            );
            const setMasterDataSettings = useAppStore(
                (state) => state.setCompanyYardiMasterDataSettings,
            );
            const setMasterDataSettingsError = useAppStore(
                (state) => state.setCompanyYardiMasterDataSettingsError,
            );
            const masterDataSyncRunsState = useAppStore(
                (state) => state.yardiMasterDataSyncRunsState,
            );
            const setSyncRunsLoading = useAppStore(
                (state) => state.setYardiMasterDataSyncRunsLoading,
            );
            const setSyncRuns = useAppStore((state) => state.setYardiMasterDataSyncRuns);
            const setSyncRunsError = useAppStore((state) => state.setYardiMasterDataSyncRunsError);

            const user = useAppStore((state) => state.user);
            const effectiveSubgroup = useAppStore((state) => state.effectiveSubgroup);
            const userPermissions = user?.permissions ?? [];

            // Mirrors the backend's require_proda_admin: an admin belonging to the
            // Proda company. Uses the same signals as the Sidebar's Proda-only
            // chrome — the auth store's isProdaUser flag (proda.ai email domain OR
            // Proda company, computed in wireRootLayout) — combined with the
            // emulation-aware admin status. The gate follows the user's REAL
            // company (like the backend), so it holds while a Proda admin is
            // viewing another company to configure its SFTP delivery.
            const isProdaAdmin =
                (user?.isProdaUser ?? false) && computeIsViewingAsAdmin(user, effectiveSubgroup);

            const loadThresholds = useCallback(async () => {
                setThresholdsLoading();
                try {
                    const data = await api.companySettings.listThresholds();
                    setThresholds(data.map(mapThreshold));
                } catch (error) {
                    setThresholdsError(
                        error instanceof Error ? error : new Error("Failed to load thresholds"),
                    );
                }
            }, [setThresholdsLoading, setThresholds, setThresholdsError]);

            const loadSftpSettings = useCallback(async () => {
                setSftpSettingsLoading();
                try {
                    const data = await api.companySettings.getSftpSettings();
                    setSftpSettings(data.settings ? mapSftpSettings(data.settings) : null);
                    setSftpEnvDefaults(mapSftpEnvDefaults(data.environment));
                } catch (error) {
                    setSftpSettingsError(
                        error instanceof Error ? error : new Error("Failed to load SFTP settings"),
                    );
                }
            }, [setSftpSettingsLoading, setSftpSettings, setSftpEnvDefaults, setSftpSettingsError]);

            const loadSftpSubmissions = useCallback(async () => {
                setSftpSubmissionsLoading();
                try {
                    const data = await api.companySettings.listSftpSubmissions();
                    setSftpSubmissions(data.map(mapSftpSubmission));
                } catch (error) {
                    setSftpSubmissionsError(
                        error instanceof Error ? error : new Error("Failed to load submissions"),
                    );
                }
            }, [setSftpSubmissionsLoading, setSftpSubmissions, setSftpSubmissionsError]);

            const loadMasterDataSettings = useCallback(async () => {
                setMasterDataSettingsLoading();
                try {
                    const data = await api.companySettings.getCompanyYardiMasterDataSettings();
                    setMasterDataSettings(
                        data.settings ? mapYardiMasterDataSettings(data.settings) : null,
                    );
                } catch (error) {
                    setMasterDataSettingsError(
                        error instanceof Error
                            ? error
                            : new Error("Failed to load master data sync settings"),
                    );
                }
            }, [setMasterDataSettingsLoading, setMasterDataSettings, setMasterDataSettingsError]);

            const loadMasterDataSyncRuns = useCallback(async () => {
                setSyncRunsLoading();
                try {
                    const data = await api.companySettings.listYardiMasterDataSyncRuns();
                    setSyncRuns(data.items.map(mapYardiMasterDataSyncRun));
                } catch (error) {
                    setSyncRunsError(
                        error instanceof Error ? error : new Error("Failed to load sync runs"),
                    );
                }
            }, [setSyncRunsLoading, setSyncRuns, setSyncRunsError]);

            const loadAll = useCallback(() => {
                void loadThresholds();
                void loadSftpSubmissions();
                // The SFTP connection and master-data sync endpoints 403 for
                // non-Proda admins — don't call them.
                if (isProdaAdmin) {
                    void loadSftpSettings();
                    void loadMasterDataSettings();
                    void loadMasterDataSyncRuns();
                }
            }, [
                loadThresholds,
                loadSftpSubmissions,
                loadSftpSettings,
                loadMasterDataSettings,
                loadMasterDataSyncRuns,
                isProdaAdmin,
            ]);

            return {
                onMount: loadAll,
                onUnmount: NOOP,
                useThresholdsState: () => thresholdsState,
                useSftpSettingsState: () => sftpSettingsState,
                useSftpEnvDefaults: () => sftpEnvDefaults,
                useSftpSubmissionsState: () => sftpSubmissionsState,
                useMasterDataSettingsState: () => masterDataSettingsState,
                useMasterDataSyncRunsState: () => masterDataSyncRunsState,
                onRefresh: loadAll,
                onCreateThreshold: useCallback(
                    async (data: {
                        start_time: string;
                        end_time: string;
                        threshold: number;
                        instance: TSftpDeliveryInstance;
                    }) => {
                        await api.companySettings.createThreshold(data);
                        await loadThresholds();
                    },
                    [loadThresholds],
                ),
                onUpdateThreshold: useCallback(
                    async (
                        id: string,
                        data: {
                            start_time?: string;
                            end_time?: string;
                            threshold?: number;
                            instance?: TSftpDeliveryInstance;
                        },
                    ) => {
                        await api.companySettings.updateThreshold(id, data);
                        await loadThresholds();
                    },
                    [loadThresholds],
                ),
                onDeleteThreshold: useCallback(
                    async (id: string) => {
                        await api.companySettings.deleteThreshold(id);
                        await loadThresholds();
                    },
                    [loadThresholds],
                ),
                onSaveSftpSettings: useCallback(
                    async (data: {
                        enabled: boolean;
                        remote_directory: string;
                        file_name_pattern: string;
                        notes: string | null;
                        host: string;
                        port: number;
                        username: string;
                        known_hosts: string;
                        password?: string | null;
                        private_key?: string | null;
                        private_key_passphrase?: string | null;
                    }) => {
                        const saved = await api.companySettings.updateSftpSettings(data);
                        setSftpSettings(mapSftpSettings(saved));
                    },
                    [setSftpSettings],
                ),
                onDownloadTransferPayload: useCallback(async (transferId: string) => {
                    await api.companySettings.downloadSftpTransferPayload(transferId);
                }, []),
                onTestSftpConnection: useCallback(async () => {
                    const result = await api.companySettings.testSftpConnection();
                    return {
                        success: result.success,
                        remoteDirectory: result.remote_directory,
                        entryCount: result.entry_count,
                        errorCode: result.error_code,
                        errorMessage: result.error_message,
                    };
                }, []),
                onSaveMasterDataSettings: useCallback(
                    async (data: { base_url: string; bearer_token?: string | null }) => {
                        const saved =
                            await api.companySettings.putCompanyYardiMasterDataSettings(data);
                        setMasterDataSettings(mapYardiMasterDataSettings(saved));
                    },
                    [setMasterDataSettings],
                ),
                onSyncMasterDataNow: useCallback(async () => {
                    await api.companySettings.triggerYardiMasterDataSync();
                    await loadMasterDataSyncRuns();
                }, [loadMasterDataSyncRuns]),
                permissions: () => userPermissions,
                isProdaAdmin: () => isProdaAdmin,
            };
        },
    });

    return { ViewCompanySettings };
};
